/**
 * Utility functions for handling money and currency operations in the application.
 */
export const MoneyUtils = {
    /**
     * Formats a number as currency with the specified currency symbol and locale.
     *
     * @param amount - The amount to format
     * @param currency - The currency code (default: 'CHF')
     * @param locale - The locale to use for formatting (default: 'de-CH')
     * @returns A formatted currency string (e.g., 'CHF 100.50' or '100,50 CHF' depending on locale)
     */
    formatCurrency(amount: number, currency: string = 'CHF', locale: string = 'de-CH'): string {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    },

    /**
     * Converts an amount from one currency to another using the provided exchange rate.
     *
     * @param amount - The amount to convert
     * @param exchangeRate - The exchange rate to apply
     * @returns The converted amount (e.g., if amount=100 and exchangeRate=1.1, returns 110)
     */
    convertCurrency(amount: number, exchangeRate: number): number {
        return amount * exchangeRate;
    },

    /**
     * Parses a currency string into a number value.
     *
     * @param currencyString - The currency string to parse (e.g., "CHF 100.50")
     * @returns The parsed number value or null if parsing fails (e.g., returns 100.50 for "CHF 100.50")
     */
    parseCurrencyString(currencyString: string): number | null {
        if (!currencyString) return null;
        const numericValue = currencyString.replace(/[^\d.-]/g, '');
        const parsedValue = parseFloat(numericValue);

        return isNaN(parsedValue) ? null : parsedValue;
    },

    /**
     * Calculates the sum of multiple monetary amounts.
     *
     * @param amounts - An array of monetary amounts
     * @returns The sum of all amounts (e.g., for [10.5, 20, 30.25] returns 60.75)
     */
    sumAmounts(amounts: number[]): number {
        return amounts.reduce((sum, amount) => sum + amount, 0);
    },

    /**
     * Formats a number as currency but returns null if the amount is 0.
     *
     * @param amount - The amount to format
     * @param currency - The currency code (default: 'CHF')
     * @param locale - The locale to use for formatting (default: 'de-CH')
     * @returns A formatted currency string or null if amount is 0 (e.g., 'CHF 100.50', null for 0)
     */
    formatNullableCurrency(amount: number, currency: string = 'CHF', locale: string = 'de-CH'): string | null {
        if (amount === 0) {
            return null;
        }
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    },

    /**
     * Rounds a number to the nearest 5 cents (0.05).
     *
     * @param amount - The amount to round
     * @returns The rounded amount as a string with 2 decimal places or null if amount is 0
     * (e.g., '10.25' for 10.23, '10.30' for 10.28, null for 0)
     */
    roundToNearestFiveCents(amount: number): string | null {
        if (amount === 0) {
            return null;
        }
        return (Math.ceil(amount * 20) / 20).toFixed(2);
    },

    /**
     * Formats a number with thousands separators using quotes.
     *
     * @param number - The number to format
     * @returns The formatted number with quote separators or null if input is falsy
     * (e.g., '1'000'000' for 1000000, null for 0 or null)
     */
    numberWithQuote(number: number | null | undefined): string | null {
        if (number === null || number === undefined) return null;
        if (number === 0) return '0';
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    },

    /**
     * Checks if a value is a valid monetary amount.
     *
     * @param value - The value to check
     * @returns True if the value is a valid number and not NaN
     */
    isValidAmount(value: number): boolean {
        return !isNaN(value);
    },
};
